import React, { useMemo, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { toPng } from 'html-to-image';
import { Wallet, Save, ShieldAlert, CheckCircle2, Star, Search, X, Share2 } from 'lucide-react';
import { formatApiError } from '../../context/AuthContext';
import FantasyCardTile, { EmptySlot } from './MiniPlayerCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BUDGET = 100;

const emptySlots = () => ({ gk: null, def: [null, null], ata: [null, null] });

/** Bulk-fetch official card data for every approved player. Returns {playerName: cardData}. */
const useCardsIndex = (players) => {
  const [cards, setCards] = useState({});
  useEffect(() => {
    if (!players.length) return;
    let alive = true;
    const missing = players.filter((p) => !cards[p.player_name]);
    if (!missing.length) return;
    Promise.all(
      missing.map((p) =>
        axios.get(`${API}/players/${encodeURIComponent(p.player_name)}/card`)
          .then((r) => [p.player_name, r.data])
          .catch(() => [p.player_name, null])
      )
    ).then((entries) => {
      if (!alive) return;
      setCards((prev) => {
        const next = { ...prev };
        for (const [name, data] of entries) if (data) next[name] = data;
        return next;
      });
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [players]);
  return cards;
};

/** Picker modal: shown when the user clicks an empty slot. */
const PlayerPickerModal = ({ open, onClose, eligible, cards, onPick, budgetLeft, slotType }) => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    return eligible.filter((p) =>
      !search || p.player_name.toLowerCase().includes(search.toLowerCase())
    );
  }, [eligible, search]);
  if (!open) return null;
  const slotLabel = { GK: 'Guarda-Redes', DEF: 'Defesa', ATA: 'Avançado' }[slotType] || 'Jogador';
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="player-picker-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-white/10 flex items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-300 font-black">Escolher {slotLabel}</div>
            <div className="text-white font-bold text-lg">Orçamento restante: <span className="text-emerald-300">{budgetLeft.toFixed(1)} €M</span></div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-white" data-testid="close-picker-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 border-b border-white/10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Procurar jogador..."
              className="w-full bg-slate-800/70 border border-white/10 rounded-lg pl-10 pr-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="picker-search-input" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="flex flex-wrap gap-4 justify-center">
            {filtered.map((p) => {
              const tooExpensive = p.current_price > budgetLeft + 0.001;
              return (
                <FantasyCardTile
                  key={p.id} player={p} cardData={cards[p.player_name]}
                  slotPos={slotType} size="picker"
                  disabled={tooExpensive}
                  onClick={() => !tooExpensive && onPick(p)}
                />
              );
            })}
          </div>
          {filtered.length === 0 && (
            <div className="text-center text-slate-500 text-sm py-8">Nenhum jogador disponível.</div>
          )}
        </div>
      </div>
    </div>
  );
};

/** Slot on the pitch — click to pick or captain-toggle. */
const PitchSlot = ({ player, cardData, slotType, captain, onPick, onRemove, onCaptain }) => {
  if (!player) return <EmptySlot label={{ GK: 'GR', DEF: 'DEF', ATA: 'ATA' }[slotType]} onClick={onPick} size="pitch" />;
  return (
    <div className="relative">
      <FantasyCardTile
        player={player} cardData={cardData} slotPos={slotType} selected captain={captain}
        size="pitch"
        onClick={onCaptain} onRemove={onRemove}
      />
      {onCaptain && (
        <div className="text-center mt-1">
          <span className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest ${captain ? 'text-amber-300' : 'text-slate-400/60'}`}>
            <Star className={`w-3 h-3 ${captain ? 'fill-current' : ''}`} /> {captain ? 'Capitão' : 'Tocar para Capitão'}
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Tactical pitch (5v5 formation 2-2-1):
 *   ATA1    ATA2
 *      DEF1    DEF2
 *          GK
 */
const TacticalPitch = ({ lineup, cards, setSlot, toggleCaptain, captainId }) => {
  return (
    <div className="relative rounded-2xl overflow-hidden border border-emerald-700/60 shadow-2xl shadow-emerald-950/40" data-testid="tactical-pitch">
      {/* Pitch background */}
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(180deg,#0e6b3d 0%,#0d5e34 50%,#0e6b3d 100%)',
      }} />
      {/* Grass stripes */}
      <div className="absolute inset-0 opacity-20"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg,rgba(255,255,255,0.15) 0px,rgba(255,255,255,0.15) 40px,transparent 40px,transparent 80px)' }} />
      {/* Field markings */}
      <svg viewBox="0 0 400 500" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
        <g stroke="white" strokeWidth="2" fill="none" opacity="0.55">
          {/* Outer boundary */}
          <rect x="10" y="10" width="380" height="480" />
          {/* Midfield line */}
          <line x1="10" y1="250" x2="390" y2="250" />
          {/* Center circle */}
          <circle cx="200" cy="250" r="55" />
          <circle cx="200" cy="250" r="3" fill="white" />
          {/* Top penalty area (opponents) */}
          <rect x="120" y="10" width="160" height="70" />
          <rect x="160" y="10" width="80" height="30" />
          {/* Top goal */}
          <rect x="180" y="4" width="40" height="6" fill="white" opacity="0.4" />
          {/* Bottom penalty area (ours) */}
          <rect x="120" y="420" width="160" height="70" />
          <rect x="160" y="460" width="80" height="30" />
          {/* Bottom goal */}
          <rect x="180" y="490" width="40" height="6" fill="white" opacity="0.4" />
        </g>
      </svg>

      {/* Slots grid — 5v5 formation 2-2-1 */}
      <div className="relative grid grid-rows-3 gap-2 sm:gap-4 py-4 sm:py-6 px-3 sm:px-8 min-h-[560px] sm:min-h-[680px]">
        {/* Attack line: 2 ATA */}
        <div className="flex items-center justify-around">
          <PitchSlot player={lineup.ata[0]} cardData={cards[lineup.ata[0]?.player_name]} slotType="ATA"
            captain={lineup.ata[0]?.id === captainId}
            onPick={() => setSlot('ata', 0, 'PICK')}
            onRemove={() => setSlot('ata', 0, null)}
            onCaptain={() => toggleCaptain(lineup.ata[0]?.id)}
          />
          <PitchSlot player={lineup.ata[1]} cardData={cards[lineup.ata[1]?.player_name]} slotType="ATA"
            captain={lineup.ata[1]?.id === captainId}
            onPick={() => setSlot('ata', 1, 'PICK')}
            onRemove={() => setSlot('ata', 1, null)}
            onCaptain={() => toggleCaptain(lineup.ata[1]?.id)}
          />
        </div>
        {/* Middle: 2 DEF */}
        <div className="flex items-center justify-around">
          <PitchSlot player={lineup.def[0]} cardData={cards[lineup.def[0]?.player_name]} slotType="DEF"
            captain={lineup.def[0]?.id === captainId}
            onPick={() => setSlot('def', 0, 'PICK')}
            onRemove={() => setSlot('def', 0, null)}
            onCaptain={() => toggleCaptain(lineup.def[0]?.id)}
          />
          <PitchSlot player={lineup.def[1]} cardData={cards[lineup.def[1]?.player_name]} slotType="DEF"
            captain={lineup.def[1]?.id === captainId}
            onPick={() => setSlot('def', 1, 'PICK')}
            onRemove={() => setSlot('def', 1, null)}
            onCaptain={() => toggleCaptain(lineup.def[1]?.id)}
          />
        </div>
        {/* Back: GK */}
        <div className="flex items-end justify-center">
          <PitchSlot player={lineup.gk} cardData={cards[lineup.gk?.player_name]} slotType="GK"
            captain={lineup.gk?.id === captainId}
            onPick={() => setSlot('gk', null, 'PICK')}
            onRemove={() => setSlot('gk', null, null)}
            onCaptain={() => toggleCaptain(lineup.gk?.id)}
          />
        </div>
      </div>
    </div>
  );
};

/** Main lineup builder using the tactical pitch. */
const LineupBuilder = ({ players, matchDayId, initialLineup, marketOpen, onSaved }) => {
  const byId = useMemo(() => Object.fromEntries(players.map((p) => [p.id, p])), [players]);
  const cards = useCardsIndex(players);
  const [searchParams, setSearchParams] = useSearchParams();
  const pitchRef = useRef(null);
  const [sharing, setSharing] = useState(false);

  // Hydrate initial state from server payload (slot-based)
  const [slots, setSlots] = useState(() => {
    if (!initialLineup) return emptySlots();
    return {
      gk: byId[initialLineup.gk_id] || null,
      def: [byId[initialLineup.def_ids?.[0]] || null, byId[initialLineup.def_ids?.[1]] || null],
      ata: [byId[initialLineup.ata_ids?.[0]] || null, byId[initialLineup.ata_ids?.[1]] || null],
    };
  });
  const [captainId, setCaptainId] = useState(initialLineup?.captain_id || '');
  const [picker, setPicker] = useState(null); // { field, index, slotType }
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [saving, setSaving] = useState(false);

  const allSelected = useMemo(() => [
    slots.gk, ...slots.def, ...slots.ata,
  ].filter(Boolean), [slots]);

  const totalCost = allSelected.reduce((s, p) => s + (p.current_price || 0), 0);
  const overBudget = totalCost > BUDGET;
  const filled = allSelected.length;
  const budgetLeft = Math.max(0, BUDGET - totalCost);
  const captainInLineup = captainId && allSelected.some((p) => p.id === captainId);
  const canSave = marketOpen && !overBudget && filled === 5 && captainInLineup;
  const bonusCount = allSelected.filter((p) => {
    if (p === slots.gk) return p.preferred_position === 'GK';
    if (slots.def.includes(p)) return p.preferred_position === 'DEF';
    if (slots.ata.includes(p)) return p.preferred_position === 'ATA';
    return false;
  }).length;

  const eligibleForPicker = useMemo(() => {
    if (!picker) return [];
    const selectedIds = new Set(allSelected.map((p) => p.id));
    // Any player can be drafted for any slot — the position bonus is only
    // awarded if they actually play in that position on match day.
    return players.filter((p) => !selectedIds.has(p.id))
      .sort((a, b) => (b.rating || 0) - (a.rating || 0));
  }, [picker, players, allSelected]);

  const setSlot = (field, index, value) => {
    setErr(''); setOk('');
    if (value === 'PICK') {
      setPicker({ field, index, slotType: field === 'gk' ? 'GK' : field.toUpperCase() });
      return;
    }
    setSlots((s) => {
      if (field === 'gk') {
        if (value === null && captainId === s.gk?.id) setCaptainId('');
        return { ...s, gk: value };
      }
      const arr = [...s[field]];
      if (value === null && captainId === arr[index]?.id) setCaptainId('');
      arr[index] = value;
      return { ...s, [field]: arr };
    });
  };

  const onPick = (p) => {
    const { field, index } = picker;
    setSlots((s) => {
      if (field === 'gk') return { ...s, gk: p };
      const arr = [...s[field]];
      arr[index] = p;
      return { ...s, [field]: arr };
    });
    setPicker(null);
  };

  const toggleCaptain = (id) => {
    if (!id) return;
    setCaptainId((c) => (c === id ? '' : id));
  };

  // Auto-insert a player when arriving via /fantasy?add=<profile_id> (from Mercado)
  useEffect(() => {
    const addId = searchParams.get('add');
    if (!addId || !byId[addId]) return;
    const target = byId[addId];
    const already = [slots.gk, ...slots.def, ...slots.ata].some((p) => p?.id === addId);
    if (already) { setSearchParams({}, { replace: true }); return; }
    setSlots((s) => {
      if (target.position_type === 'GK') {
        if (!s.gk) return { ...s, gk: target };
        return s;
      }
      // LINE — find first empty DEF then ATA slot based on preferred position
      const preferAta = target.preferred_position === 'ATA';
      const order = preferAta ? ['ata', 'def'] : ['def', 'ata'];
      for (const field of order) {
        const arr = [...s[field]];
        const idx = arr.findIndex((x) => !x);
        if (idx !== -1) {
          arr[idx] = target;
          return { ...s, [field]: arr };
        }
      }
      return s;
    });
    setOk(`${target.player_name} adicionado à escalação!`);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, byId]);

  const sharePng = async () => {
    if (!pitchRef.current) return;
    setSharing(true); setErr('');
    try {
      const dataUrl = await toPng(pitchRef.current, {
        pixelRatio: 2,
        backgroundColor: '#0f172a',
        cacheBust: true,
      });
      // Try Web Share API (mobile) with file support
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], 'escalacao.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'A minha escalação · RatingPro Fantasy', text: 'A minha equipa desta jornada 🔥⚽' });
      } else {
        // Fallback: download
        const a = document.createElement('a');
        a.href = dataUrl; a.download = 'escalacao-ratingpro-fantasy.png';
        document.body.appendChild(a); a.click(); a.remove();
      }
    } catch (ex) {
      setErr('Não foi possível gerar a imagem. Tenta novamente.');
    } finally { setSharing(false); }
  };

  const save = async () => {
    setErr(''); setOk(''); setSaving(true);
    try {
      const { data } = await axios.post(`${API}/fantasy/my-lineup`, {
        match_day_id: matchDayId,
        gk_id: slots.gk.id,
        def_ids: slots.def.map((p) => p.id),
        ata_ids: slots.ata.map((p) => p.id),
        captain_id: captainId,
      });
      setOk('Escalação guardada com sucesso!');
      onSaved && onSaved(data);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* LEFT: Pitch */}
      <section className="lg:col-span-2">
        <div ref={pitchRef}>
          <TacticalPitch lineup={slots} cards={cards} setSlot={setSlot} toggleCaptain={toggleCaptain} captainId={captainId} />
        </div>
        <p className="text-center text-xs text-slate-400 mt-3">
          Toca num slot vazio para escolher jogador · toca num jogador escalado para o definir como <span className="text-amber-300 font-bold">Capitão</span> · a estrela <Star className="w-3 h-3 inline fill-amber-300 text-amber-300" /> marca o Capitão.
        </p>
      </section>

      {/* RIGHT: Info panel */}
      <aside className="lg:col-span-1 space-y-4">
        <div className="bg-slate-900/60 border border-white/10 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-slate-300 text-sm font-medium">
              <Wallet className="w-4 h-4" /> Orçamento
            </div>
            <div className={`text-sm font-black ${overBudget ? 'text-red-400' : 'text-emerald-300'}`} data-testid="lineup-budget">
              {totalCost.toFixed(1)} / {BUDGET} €M
            </div>
          </div>
          <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
            <div className={`h-full transition-all ${overBudget ? 'bg-red-500' : 'bg-emerald-500'}`}
              style={{ width: `${Math.min(100, (totalCost / BUDGET) * 100)}%` }} />
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4 text-xs">
            <div className="text-center">
              <div className="text-slate-400">Slots</div>
              <div className={`font-black text-base ${filled === 5 ? 'text-emerald-400' : 'text-white'}`}>{filled}/5</div>
            </div>
            <div className="text-center">
              <div className="text-slate-400">Bónus posição</div>
              <div className="font-black text-base text-emerald-300" data-testid="bonus-count">+{bonusCount.toFixed(1)}</div>
            </div>
            <div className="text-center">
              <div className="text-slate-400">Capitão</div>
              <div className={`font-black text-base ${captainInLineup ? 'text-amber-300' : 'text-slate-500'}`}>{captainInLineup ? 'Sim' : '—'}</div>
            </div>
          </div>

          {err && <div className="mt-3 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="lineup-error">{err}</div>}
          {ok && <div className="mt-3 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="lineup-success"><CheckCircle2 className="w-4 h-4" /> {ok}</div>}
          {!marketOpen && (
            <div className="mt-3 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="market-closed-warning">
              <ShieldAlert className="w-4 h-4" /> Mercado fechado.
            </div>
          )}

          <button
            disabled={!canSave || saving}
            onClick={save}
            className="w-full mt-4 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-black rounded-lg py-2.5 flex items-center justify-center gap-2"
            data-testid="lineup-save-btn"
          >
            <Save className="w-4 h-4" /> {saving ? 'A guardar...' : 'Guardar Escalação'}
          </button>

          <button
            type="button" onClick={sharePng} disabled={sharing || filled === 0}
            className="w-full mt-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed border border-emerald-500/30 text-emerald-300 font-black rounded-lg py-2.5 flex items-center justify-center gap-2 text-sm"
            data-testid="lineup-share-btn"
            title="Gerar imagem da escalação para partilhar no WhatsApp"
          >
            <Share2 className="w-4 h-4" /> {sharing ? 'A gerar...' : 'Partilhar no WhatsApp'}
          </button>
        </div>

        {/* Bonus explainer */}
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-4 text-xs text-emerald-200/80">
          <div className="flex items-center gap-2 font-black text-emerald-300 mb-2">
            <span className="inline-flex items-center bg-emerald-500 text-slate-900 text-[10px] px-1.5 py-0.5 rounded font-black">+1.0</span>
            Bónus de Posição
          </div>
          <p>Podes escalar <b>qualquer jogador em qualquer slot</b>. Se ele realmente jogar nessa posição em pelo menos uma das partidas do dia (registada no Jogo Ao Vivo), ganhas <b>+1.0 ponto</b> extra. É um aposta — arrisca!</p>
        </div>
      </aside>

      {/* Player picker modal */}
      <PlayerPickerModal
        open={!!picker}
        onClose={() => setPicker(null)}
        eligible={eligibleForPicker}
        cards={cards}
        onPick={onPick}
        budgetLeft={budgetLeft}
        slotType={picker?.slotType}
      />
    </div>
  );
};

export default LineupBuilder;
